import React, { useEffect, useState } from "react";
import { api, analyzeStream, analyzeCompareStream, analyzePackStream, getPdfTotalPages } from "@/lib/api";
import { UsageCost } from "@/components/UsageCost";
import { StatBlockEditor, cleanStats } from "@/components/StatBlockEditor";
import { CalloutFeedback } from "@/components/CalloutFeedback";
import { CardSourcePicker } from "@/components/CardSourcePicker";
import { DraftComposer } from "@/components/DraftComposer";
import { RuleCardsBadge } from "@/components/RuleCardsBadge";
import { RevisionSuggestion } from "@/components/RevisionSuggestion";
import { useShared, useSharedDraft, EditedBy, SharedDraftProvider, RunningBy, TypingMark, useRunWatcher } from "@/lib/sharedDraft";
import { DraftPresence } from "@/components/DraftPresence";
import { WordingFeedbackProvider } from "@/components/WordingFeedback";
import { CalloutFeedbackProvider, feedbackKey, isNegative, useVerdict } from "@/lib/calloutFeedback";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Loader2,
  FlaskConical,
  AlertTriangle,
  Check,
  Sparkles,
  BookOpen,
  ScanSearch,
  Brain,
  ShieldAlert,
  FileText,
  History,
  Plus,
  X,
  GitCompare,
  ScrollText,
  Trophy,
  Layers,
  MessageSquare,
  Trash2,
  Wand2,
} from "lucide-react";
import { sectionBadgeColor, sectionLabel } from "@/components/RuleComponents";
import { useNav, VIEWS } from "@/lib/nav";
import { TagsInput } from "@/components/TagsInput";
import { AsyncCombobox } from "@/components/AsyncCombobox";
import { LinkifiedText } from "@/components/LinkifiedText";
import { ModelTypeSelect } from "@/components/ModelTypeSelect";
import { buildPlaytestReport, downloadText, slugify } from "@/lib/playtestReport";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, Download } from "lucide-react";

const EXAMPLES = [
  "When this model is hit by a ranged attack, it can advance up to 2 inches directly toward the attacker before the damage roll is resolved.",
  "This model gains +2 ARM against melee attacks while it has 1 or more focus points on it.",
  "At the end of this model\u2019s activation, if it destroyed one or more enemy models during this activation, it can make one additional ranged attack.",
];

// Rule-type options (kept in sync with backend api/llm.py:RULE_TYPE_LABELS)
export const RULE_TYPES = [
  { id: "modelAbility",  label: "Model Ability",  hint: "Printed in the abilities band on a model card" },
  { id: "cardAbility",   label: "Card Ability",   hint: "Applies to the whole unit / card" },
  { id: "weaponAbility", label: "Weapon Quality", hint: "Printed in a weapon stat block" },
  { id: "spell",         label: "Spell",          hint: "Cast at FOCUS cost / CAST action" },
  { id: "animus",        label: "Animus",         hint: "Warbeast power, can be transferred" },
  { id: "feat",          label: "Feat / Special Action", hint: "Warcaster feat or one-shot action" },
  { id: "other",         label: "Other",          hint: "General special rule" },
];

export const ruleTypeLabel = (id) =>
  (RULE_TYPES.find((t) => t.id === id) || {}).label || id;

const STAGES = [
  { id: "textual", label: "Textual scan", icon: ScanSearch, desc: "Match 2,262 existing rules by wording (difflib)." },
  { id: "semantic", label: "Semantic match", icon: Brain, desc: "The LLM finds rules that mean the same thing." },
  { id: "conflicts", label: "Rule conflicts", icon: ShieldAlert, desc: "Cross-check the main rulebook + Steamroller." },
  { id: "summary", label: "QA summary", icon: FileText, desc: "Final write-up for the editor." },
];

export function ProposePage() {
  const { navigate } = useNav();
  const [mode, setModeRaw] = useState("model"); // "model" | "quick" | "draft" | "compare"
  const [modelSeed, setModelSeed] = useState(null);  // prefill from "Write from intent"
  const [text, setText] = useShared("quick_text", "");
  const [altText, setAltText] = useShared("quick_alt_text", "");
  const [name, setName] = useShared("quick_name", "");
  const [tags, setTags] = useShared("quick_tags", []);
  const [ruleStats, setRuleStats] = useShared("quick_rule_stats", {});
  const [ruleType, setRuleType] = useShared("quick_rule_type", ""); // "" | modelAbility | cardAbility | weaponAbility | spell | animus | feat | other
  const [modelTypes, setModelTypes] = useShared("quick_model_types", []); // ids from /api/model-types
  const [intent, setIntent] = useShared("quick_intent", ""); // free-text author intent
  const [showAlt, setShowAlt] = useShared("quick_show_alt", false);
  const setMode = (m) => {
    setModeRaw(m);
    if (m === "compare") setShowAlt(true);
    if (m === "quick") { setShowAlt(false); setAltText(""); setAltRule(null); }
  };
  // Polling signal used by Cancel button. We toggle .cancelled=true and call
  // the backend cancel endpoint so LLM cost stops too.
  const [cancelSignal, setCancelSignal] = useState(null);
  const [activeJobId, setActiveJobId] = useShared("quick_job_id", null);
  // "manual" (user types alt) or "rule" (pick existing rule as side B)
  const [altMode, setAltMode] = useShared("quick_alt_mode", "manual");
  const [altRule, setAltRule] = useShared("quick_alt_rule", null); // {id, label, text}
  const [loading, setLoading] = useShared("quick_loading", false);
  const [stageStatus, setStageStatus] = useShared("quick_stage_status", {});
  const [partial, setPartial] = useShared("quick_partial", {
    fast_similar: [],
    semantic_similar: [],
    conflicts: [],
  });
  const [result, setResult] = useShared("quick_result", null);
  const [altResult, setAltResult] = useShared("quick_alt_result", null);
  const [comparison, setComparison] = useShared("quick_comparison", null);
  const [compareStages, setCompareStages] = useShared("quick_compare_stages", {
    stages_a: {}, stages_b: {},
    partial_a: { fast_similar: [], semantic_similar: [], conflicts: [] },
    partial_b: { fast_similar: [], semantic_similar: [], conflicts: [] },
  });
  const [error, setError] = useShared("quick_error", null);
  const [elapsed, setElapsed] = useState(0);
  const [savedId, setSavedId] = useShared("quick_saved_id", null);

  useEffect(() => {
    if (!loading) return;
    setElapsed(0);
    const start = Date.now();
    const t = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 500);
    return () => clearInterval(t);
  }, [loading]);

  const resetState = () => {
    setError(null);
    setResult(null);
    setAltResult(null);
    setComparison(null);
    setSavedId(null);
    setStageStatus({});
    setPartial({ fast_similar: [], semantic_similar: [], conflicts: [] });
    setCompareStages({
      stages_a: {}, stages_b: {},
      partial_a: { fast_similar: [], semantic_similar: [], conflicts: [] },
      partial_b: { fast_similar: [], semantic_similar: [], conflicts: [] },
    });
  };

  const persistProposal = async (payload) => {
    try {
      const saved = await api.saveProposal(payload);
      setSavedId(saved.id);
    } catch (e) {
      console.warn("save proposal failed", e);
      setError("The check finished but could not be saved to History — try running it again.");
    }
  };

  const effectiveAltText = altMode === "rule" ? (altRule?.text || "") : altText;

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setLoading(true);
    resetState();
    try {
      // Create a fresh cancel signal for this run.
      const signal = { cancelled: false };
      setCancelSignal(signal);
      setActiveJobId(null);
      if (showAlt && effectiveAltText.trim()) {
        const cmp = await analyzeCompareStream(text.trim(), effectiveAltText.trim(), (job, jobId) => {
          if (jobId && !activeJobId) setActiveJobId(jobId);
          setCompareStages({
            stages_a: job.stages_a || {},
            stages_b: job.stages_b || {},
            partial_a: job.partial_a || { fast_similar: [], semantic_similar: [], conflicts: [] },
            partial_b: job.partial_b || { fast_similar: [], semantic_similar: [], conflicts: [] },
          });
          if (job.llm_error) setError(job.llm_error);
        }, ruleType || null, modelTypes, intent || null, signal, cleanStats(ruleType, ruleStats));
        setResult(cmp.result_a);
        setAltResult(cmp.result_b);
        setComparison(cmp.comparison);
        await persistProposal({
          proposed_text: text.trim(),
          alternative_text: effectiveAltText.trim(),
          result: cmp.result_a,
          alternative_result: cmp.result_b,
          comparison: cmp.comparison,
          name: name.trim() || (altMode === "rule" && altRule ? `vs. ${altRule.label}` : null),
          tags,
          rule_type: ruleType || null,
          model_types: modelTypes,
          intent: intent || null,
          rule_stats: cleanStats(ruleType, ruleStats),
        });
      } else {
        const finalResult = await analyzeStream(text.trim(), (job, jobId) => {
          if (jobId && !activeJobId) setActiveJobId(jobId);
          setStageStatus(job.stages);
          setPartial(job.partial);
          if (job.llm_error) setError(job.llm_error);
        }, ruleType || null, modelTypes, intent || null, signal, cleanStats(ruleType, ruleStats));
        setResult(finalResult);
        await persistProposal({
          proposed_text: text.trim(),
          result: finalResult,
          name: name.trim() || null,
          tags,
          rule_type: ruleType || null,
          model_types: modelTypes,
          intent: intent || null,
          rule_stats: cleanStats(ruleType, ruleStats),
        });
      }
    } catch (e) {
      if (e?.cancelled) {
        setError("Analysis cancelled.");
      } else {
        setError(e?.message || "Analysis failed");
      }
    } finally {
      setLoading(false);
      setCancelSignal(null);
      setActiveJobId(null);
    }
  };

  const handleCancel = async () => {
    if (cancelSignal) cancelSignal.cancelled = true;
    if (activeJobId) {
      try { await api.cancelAnalyze(activeJobId); } catch (e) { console.warn("Cancel analyze failed (best-effort)", e); }
    }
    setError("Analysis cancelled.");
    setLoading(false);
  };

  useRunWatcher({
    loading, localRunning: Boolean(cancelSignal), kind: "analyze", jobId: activeJobId,
    onComplete: (job) => {
      if (job.mode === "compare") {
        setResult(job.result_a); setAltResult(job.result_b); setComparison(job.comparison);
      } else {
        setStageStatus(job.stages); setPartial(job.partial); setResult(job.result);
      }
      setLoading(false); setActiveJobId(null);
    },
    onStop: (msg) => { setError(msg); setLoading(false); setActiveJobId(null); },
  });

  const q = { altMode, altResult, altRule, altText, compareStages, comparison, effectiveAltText, elapsed, error, handleCancel, handleSubmit, intent, loading, mode, modelTypes, name, navigate, partial, result, ruleStats, ruleType, savedId, setAltMode, setAltRule, setAltText, setIntent, setModelTypes, setName, setRuleStats, setRuleType, setShowAlt, setTags, setText, showAlt, stageStatus, tags, text };
  return (
    <div className="grid gap-6" data-testid="propose-page">
      <ModeToggle mode={mode} setMode={setMode} />

      <DraftPresence mode={mode} />
      <div className={mode === "model" ? "" : "hidden"}>
        <PackComposer seed={modelSeed} />
      </div>
      <div className={mode === "draft" ? "" : "hidden"}>
        <DraftComposer
          ruleTypes={RULE_TYPES}
          onUse={(seed) => { setModelSeed({ ...seed, key: crypto.randomUUID() }); setModeRaw("model"); }}
        />
      </div>
      <div className={mode === "quick" || mode === "compare" ? "grid gap-6" : "hidden"}>
      <QuickCheckForm q={q} />
      <QuickCheckResults q={q} />
      </div>
    </div>
  );
}

/** Quick check / Compare: the wording inputs. */
function QuickCheckForm({ q }) {
  const { altMode, altRule, altText, effectiveAltText, elapsed, handleCancel, handleSubmit, intent, loading, mode, modelTypes, name, navigate, ruleStats, ruleType, setAltMode, setAltRule, setAltText, setIntent, setModelTypes, setName, setRuleStats, setRuleType, setShowAlt, setTags, setText, showAlt, tags, text } = q;
  return (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">{mode === "compare" ? "Compare two wordings" : "Quick check"}</h2>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(VIEWS.history())}
          className="gap-1.5"
          data-testid="open-history-btn"
        >
          <History className="w-4 h-4" /> History
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {mode === "compare"
          ? "Two phrasings of the same rule, QA’d side by side with an LLM verdict on which is cleaner."
          : "The fast “let’s just check” option: paste a wording (no model card) and we flag similar existing rules and rulebook + Steamroller conflicts. Saved to History under the name you give it."}
      </p>

      <div className={showAlt ? "grid md:grid-cols-2 gap-3" : ""}>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1 block">
            {showAlt ? "Wording A" : "Proposed rule"} <EditedBy field="quick_text" className="normal-case tracking-normal ml-1" />
          </label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste or type the proposed rule wording…"
            className="min-h-[120px] font-sans"
            disabled={loading}
            data-testid="propose-textarea"
          />
        </div>
        {showAlt && (
          <div>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Wording B <EditedBy field="quick_alt_text" className="normal-case tracking-normal ml-1" /></label>
              <div className="flex items-center gap-1 text-[11px]">
                <button
                  onClick={() => setAltMode("manual")}
                  className={`px-2 py-0.5 rounded border transition ${altMode === "manual" ? "border-primary/60 bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                  disabled={loading}
                  data-testid="alt-mode-manual"
                >Type wording</button>
                <button
                  onClick={() => setAltMode("rule")}
                  className={`px-2 py-0.5 rounded border transition ${altMode === "rule" ? "border-primary/60 bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                  disabled={loading}
                  data-testid="alt-mode-rule"
                >Pick existing rule</button>
                <button
                  onClick={() => { setShowAlt(false); setAltText(""); setAltRule(null); }}
                  className="text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5 ml-1"
                  disabled={loading}
                  data-testid="remove-alternative-btn"
                >
                  <X className="w-3 h-3" /> remove
                </button>
              </div>
            </div>
            {altMode === "manual" ? (
              <Textarea
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                placeholder="Paste an alternative wording to compare…"
                className="min-h-[120px] font-sans"
                disabled={loading}
                data-testid="propose-alt-textarea"
              />
            ) : (
              <div className="space-y-2">
                <AsyncCombobox
                  placeholder="Search and pick an existing rule…"
                  triggerLabel={altRule ? altRule.label : "Pick existing rule…"}
                  icon={<ScrollText className="w-4 h-4 text-purple-400" />}
                  fetcher={async (q) => {
                    const data = await api.listRules({ q, limit: 12 });
                    return data.items.map((r) => ({
                      id: r.id,
                      name: r.name,
                      label: r.name,
                      sub: `${sectionLabel(r.section)} · ${r.text.slice(0, 60)}…`,
                      text: r.text,
                    }));
                  }}
                  onSelect={(it) => setAltRule(it)}
                  testId="pick-alt-rule"
                />
                {altRule && (
                  <div className="p-3 rounded border border-cyan-700/40 bg-cyan-950/15 text-sm space-y-1" data-testid="alt-rule-preview">
                    <div className="flex items-center justify-between gap-2">
                      <strong>{altRule.label}</strong>
                      <button
                        onClick={() => setAltRule(null)}
                        className="text-xs text-muted-foreground hover:text-rose-300 inline-flex items-center gap-0.5"
                        disabled={loading}
                      ><X className="w-3 h-3" /> clear</button>
                    </div>
                    <p className="text-xs leading-relaxed">{altRule.text}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rule type + name + tags (used for the auto-saved history entry) */}
      <div className="mt-3 grid md:grid-cols-3 gap-2">
        <Select value={ruleType} onValueChange={setRuleType} disabled={loading}>
          <SelectTrigger
            className="w-full"
            data-testid="rule-type-select"
            aria-label="Rule type"
          >
            <SelectValue placeholder="Rule type — guides the LLM…" />
          </SelectTrigger>
          <SelectContent>
            {RULE_TYPES.map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`rule-type-option-${t.id}`}>
                <span className="font-medium">{t.label}</span>
                <span className="text-[11px] text-muted-foreground ml-2">{t.hint}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Optional name for the history log…"
          disabled={loading}
          data-testid="propose-name-input"
        />
        <TagsInput
          value={tags}
          onChange={setTags}
          testId="propose-tags"
          placeholder="Add tags (e.g. errata, needs-review)…"
          disabled={loading}
        />
      </div>
      <div className="mt-3 empty:hidden">
        <StatBlockEditor ruleType={ruleType} value={ruleStats} onChange={setRuleStats} disabled={loading} testId="propose-stat-block" />
      </div>

      {/* Model type(s) — multi-select with rulebook trait notes */}
      <div className="mt-3">
        <ModelTypeSelect
          value={modelTypes}
          onChange={setModelTypes}
          disabled={loading}
          testId="propose-model-types"
        />
      </div>

      {/* Intent — author's free-text hint about what the rule should/shouldn't do */}
      <div className="mt-3">
        <details className="rounded border border-border bg-card/30">
          <summary className="cursor-pointer px-3 py-2 text-xs text-muted-foreground hover:text-foreground select-none" data-testid="propose-intent-toggle">
            <MessageSquare className="w-3 h-3 inline mr-1" />
            Rule intent <span className="opacity-70">(optional — what it should/shouldn’t do, suspected conflicts; gives the LLM a sharper target)</span>
          </summary>
          <div className="p-3 pt-1">
            <Textarea
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              placeholder={`What this rule is meant to do: …\nWhat it should NOT do: …\nRules it may interact with / suspected conflicts: …`}
              className="min-h-[90px] font-sans text-sm"
              disabled={loading}
              data-testid="propose-intent-textarea"
            />
          </div>
        </details>
      </div>

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <Button
          onClick={handleSubmit}
          disabled={loading || !text.trim() || (showAlt && !effectiveAltText.trim())}
          className="gap-2"
          data-testid="analyze-btn"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing… {elapsed}s</>
          ) : showAlt && effectiveAltText.trim() ? (
            <><GitCompare className="w-4 h-4" /> Compare A vs B</>
          ) : (
            <><Sparkles className="w-4 h-4" /> Analyze rule</>
          )}
        </Button>
        <RunningBy field="quick_loading" />
        {loading && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            className="gap-1.5 text-rose-300 border-rose-700/60 hover:bg-rose-950/30"
            data-testid="analyze-cancel-btn"
          >
            <X className="w-3.5 h-3.5" /> Cancel
          </Button>
        )}
        {!showAlt && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAlt(true)}
            disabled={loading}
            className="gap-1.5"
            data-testid="add-alternative-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add alternative wording
          </Button>
        )}
        <span className="text-xs text-muted-foreground">
          Tip: try one of these examples →
        </span>
        {EXAMPLES.map((ex, i) => (
          <button
            key={ex}
            onClick={() => setText(ex)}
            className="text-xs text-cyan-400 hover:text-cyan-300 underline-offset-2 hover:underline"
            disabled={loading}
            data-testid={`example-shortcut-${i + 1}`}
          >
            example {i + 1}
          </button>
        ))}
      </div>
    </CardContent>
  </Card>
  );
}

/** Quick check / Compare: live progress, errors and results. */
function QuickCheckResults({ q }) {
  const { altResult, compareStages, comparison, effectiveAltText, elapsed, error, intent, loading, modelTypes, name, partial, result, ruleStats, ruleType, savedId, setAltMode, setAltText, setShowAlt, showAlt, stageStatus, tags, text } = q;
  return (
    <>
      {/* Single-mode live progress */}
      {loading && !showAlt && (
        <StageTracker stageStatus={stageStatus} partial={partial} elapsed={elapsed} />
      )}
      {/* Compare mode loading: show parallel progress for both sides */}
      {loading && showAlt && (
        <Card className="border-primary/40" data-testid="compare-progress">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary animate-pulse" />
                Comparing A vs B
              </h3>
              <span className="text-xs text-muted-foreground tabular-nums">{elapsed}s elapsed</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <MiniStageList
                label="Wording A"
                stages={compareStages.stages_a}
                partial={compareStages.partial_a}
                testIdPrefix="a"
              />
              <MiniStageList
                label="Wording B"
                stages={compareStages.stages_b}
                partial={compareStages.partial_b}
                testIdPrefix="b"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert variant="destructive" data-testid="analyze-error">
          <AlertTriangle className="w-4 h-4" />
          <AlertTitle>Analysis failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Single result */}
      {result && !altResult && (
        <AnalysisResult
          result={result}
          savedId={savedId}
          exportContext={{
            name: name.trim() || null,
            tags,
            rule_type: ruleType || null,
            model_types: modelTypes,
            intent: intent || null,
            rule_stats: cleanStats(ruleType, ruleStats),
            proposed_text: text.trim(),
          }}
          onUseAsSideB={(rev) => {
            setAltText(rev);
            setAltMode("manual");
            setShowAlt(true);
            // Smooth scroll to the alt textarea
            setTimeout(() => {
              const el = document.querySelector('[data-testid="propose-alt-textarea"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 50);
          }}
        />
      )}

      {/* Comparison result (A vs B side-by-side) */}
      {result && altResult && (
        <ComparisonView
          textA={text}
          textB={effectiveAltText}
          resultA={result}
          resultB={altResult}
          comparison={comparison}
          savedId={savedId}
        />
      )}
    </>
  );
}

/** Propose Rule with its fields shared live across the workgroup and kept while you browse other tabs. */
export function ProposeWorkspace() {
  return (
    <SharedDraftProvider scope="propose">
      <WordingFeedbackProvider>
        <ProposePage />
      </WordingFeedbackProvider>
    </SharedDraftProvider>
  );
}

const MODES = [
  { id: "model", label: "Model check", icon: Layers, hint: "New rule(s) on a model card — checks the rulebook and the card's existing rules." },
  { id: "quick", label: "Quick check", icon: FlaskConical, hint: "Just the wording — fast conflict and similarity check." },
  { id: "draft", label: "Write from intent", icon: Wand2, hint: "Describe what you want; the LLM drafts three wordings." },
  { id: "compare", label: "Compare", icon: GitCompare, hint: "Two wordings side by side." },
];

function ModeToggle({ mode, setMode }) {
  const active = MODES.find((m) => m.id === mode);
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="propose-mode-toggle">
      {MODES.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => setMode(id)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors border ${
            mode === id ? "border-primary/60 bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"
          }`}
          data-testid={`propose-mode-${id}`}
        >
          <Icon className="w-3.5 h-3.5 inline mr-1.5" />
          {label}
        </button>
      ))}
      <span className="text-[11px] text-muted-foreground ml-auto">{active?.hint}</span>
    </div>
  );
}

function MiniStageList({ label, stages, partial, testIdPrefix }) {
  const items = [
    { id: "textual", label: "Textual", count: partial.fast_similar.length },
    { id: "semantic", label: "Semantic", count: partial.semantic_similar.length },
    { id: "conflicts", label: "Conflicts", count: partial.conflicts.length },
    { id: "summary", label: "Summary", count: null },
  ];
  return (
    <div className="space-y-1" data-testid={`compare-stages-${testIdPrefix}`}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
      {items.map((it) => {
        const status = stages[it.id] || "pending";
        let dot, klass;
        if (status === "running") { dot = <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />; klass = "border-primary/40 bg-primary/5"; }
        else if (status === "done") { dot = <Check className="w-3.5 h-3.5 text-emerald-400" />; klass = "border-emerald-700/40 bg-emerald-950/15"; }
        else { dot = <span className="w-2 h-2 rounded-full bg-muted-foreground/40 inline-block" />; klass = "border-border opacity-60"; }
        return (
          <div
            key={it.id}
            className={`flex items-center gap-2 px-2 py-1.5 rounded border text-xs ${klass}`}
            data-testid={`compare-stage-${testIdPrefix}-${it.id}`}
            data-status={status}
          >
            <span className="flex-1">{it.label}</span>
            {it.count !== null && status !== "pending" && (
              <Badge variant="outline" className="text-[10px] py-0">{it.count}</Badge>
            )}
            {dot}
          </div>
        );
      })}
    </div>
  );
}

function StageTracker({ stageStatus, partial, elapsed }) {
  return (
    <Card className="border-primary/40" data-testid="stage-tracker">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary animate-pulse" />
            Live QA progress
          </h3>
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="stage-elapsed">
            {elapsed}s elapsed
          </span>
        </div>
        <div className="space-y-2">
          {STAGES.map((stage) => {
            const status = stageStatus[stage.id] || "pending";
            const Icon = stage.icon;
            let dot, klass;
            if (status === "running") {
              dot = <Loader2 className="w-4 h-4 animate-spin text-primary" />;
              klass = "border-primary/50 bg-primary/5";
            } else if (status === "done") {
              dot = <Check className="w-4 h-4 text-emerald-400" />;
              klass = "border-emerald-700/40 bg-emerald-950/15";
            } else {
              dot = <span className="w-2 h-2 rounded-full bg-muted-foreground/40 inline-block" />;
              klass = "border-border opacity-60";
            }
            const count =
              stage.id === "textual" ? partial.fast_similar.length
              : stage.id === "semantic" ? partial.semantic_similar.length
              : stage.id === "conflicts" ? partial.conflicts.length
              : null;
            return (
              <div
                key={stage.id}
                className={`flex items-center gap-3 p-2.5 rounded border transition ${klass}`}
                data-testid={`stage-${stage.id}`}
                data-status={status}
              >
                <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{stage.label}</p>
                  <p className="text-[11px] text-muted-foreground">{stage.desc}</p>
                </div>
                {count !== null && status !== "pending" && (
                  <Badge variant="outline" className="text-xs">
                    {count} found
                  </Badge>
                )}
                <div className="shrink-0">{dot}</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function resultFeedbackKeys(result) {
  return [
    ...(result.conflicts || []).map(feedbackKey.conflict),
    ...(result.semantic_similar || []).map(feedbackKey.similar),
    ...(result.fast_similar || []).map(feedbackKey.similar),
  ];
}

function AnalysisResult(props) {
  const { result, exportContext } = props;
  return (
    <CalloutFeedbackProvider
      scopeText={exportContext?.proposed_text || result.proposed_text}
      ruleType={exportContext?.rule_type || result.rule_type}
      keys={resultFeedbackKeys(result)}
    >
      <AnalysisResultBody {...props} />
    </CalloutFeedbackProvider>
  );
}

function AnalysisResultBody({ result, savedId, exportContext, onUseAsSideB, persistRevision }) {
  const totalFindings = result.semantic_similar.length + result.conflicts.length;
  return (
    <div className="space-y-5" data-testid="analysis-result">
      {savedId && (
        <p className="text-xs text-muted-foreground" data-testid="saved-indicator">
          <Check className="w-3 h-3 text-emerald-400 inline mr-1" />
          Saved to history (id <code>{savedId}</code>)
        </p>
      )}
      {result.summary && (
        <Card className="border-primary/40">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <h3 className="font-semibold">QA Summary</h3>
              {result.rule_type && (
                <Badge
                  variant="outline"
                  className="ml-2 text-violet-300 border-violet-700/60 bg-violet-950/30 text-[10px]"
                  data-testid={`result-rule-type-${result.rule_type}`}
                >
                  Analyzed as {ruleTypeLabel(result.rule_type)}
                </Badge>
              )}
              <Button
                size="sm"
                variant="outline"
                className="ml-auto gap-2"
                data-testid="export-playtest-report-btn"
                onClick={() => {
                  const ctx = exportContext || {};
                  const report = buildPlaytestReport({
                    name: ctx.name || null,
                    tags: ctx.tags || [],
                    rule_type: ctx.rule_type || result.rule_type || null,
                    created_at: new Date().toISOString(),
                    proposed_text: ctx.proposed_text || result.proposed_text || "",
                    alternative_text: ctx.alternative_text || null,
                    result,
                    alternative_result: ctx.alternative_result || null,
                    comparison: ctx.comparison || null,
                    llm_model: result?.llm?.model || "",
                  });
                  const filename = `playtest-${slugify(ctx.name || "proposal")}-${new Date().toISOString().slice(0, 10)}.txt`;
                  downloadText(filename, report);
                }}
              >
                <Download className="w-3.5 h-3.5" /> Export playtest report
              </Button>
            </div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="qa-summary">
              {result.summary}
            </p>
          </CardContent>
        </Card>
      )}
      <UsageCost usage={result.usage} />
      {result.llm_error && (
        <Alert variant="destructive">
          <AlertTriangle className="w-4 h-4" />
          <AlertTitle>LLM error</AlertTitle>
          <AlertDescription>{result.llm_error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold mb-1 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" />
            Semantically similar existing rules
            <Badge variant="outline" className="ml-1">{result.semantic_similar.length}</Badge>
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            Rules that <em>mean the same thing</em> as your proposal (LLM-judged, score &ge; 0.5).
          </p>
          {result.semantic_similar.length === 0 ? (
            <p className="text-sm text-muted-foreground italic" data-testid="semantic-empty">
              <Check className="w-4 h-4 text-emerald-500 inline mr-1" />
              No semantically equivalent rule found.
            </p>
          ) : (
            <div className="space-y-3" data-testid="semantic-similar-list">
              {result.semantic_similar.map((s) => (
                <SimilarRuleRow key={s.rule.id} entry={s} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold mb-1 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-cyan-400" />
            Textually similar wordings
            <Badge variant="outline" className="ml-1">{result.fast_similar.length}</Badge>
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            Quick difflib pass: rules with overlapping word-level phrasing.
          </p>
          {result.fast_similar.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No close textual matches.</p>
          ) : (
            <div className="space-y-3" data-testid="fast-similar-list">
              {result.fast_similar.slice(0, 8).map((s) => (
                <SimilarRuleRow key={s.rule.id + "-fast"} entry={s} testSuffix="-fast" />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConflictsPanel conflicts={result.conflicts || []} />

      <RevisionSuggestion
        proposedText={exportContext?.proposed_text || result.proposed_text}
        ruleType={exportContext?.rule_type || result.rule_type}
        modelTypes={exportContext?.model_types || result.model_types || []}
        intent={exportContext?.intent || result.intent || null}
        ruleStats={exportContext?.rule_stats || null}
        conflicts={result.conflicts || []}
        existing={result.revision_suggestion}
        savedId={savedId}
        persist={persistRevision}
        onSave={(rev) => {
          // attach to result so the playtest report can include it
          result.revision_suggestion = rev;
        }}
        onUseAsSideB={onUseAsSideB}
      />

      {totalFindings === 0 && result.fast_similar.length === 0 && (
        <Card className="border-emerald-700/40">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-emerald-400">
              <Check className="w-5 h-5" />
              <p className="font-medium">Looks clean — no similar rules and no rulebook conflicts.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SimilarRuleRow({ entry, testSuffix = "" }) {
  const key = feedbackKey.similar(entry);
  const verdict = useVerdict(key);
  return (
    <div className={`rounded-md border border-border bg-background/40 p-3 transition-opacity ${isNegative(verdict) ? "opacity-55" : ""}`}>
      <SimilarRuleButton entry={entry} />
      <CalloutFeedback
        kind="similar"
        targetKey={key}
        label={`${entry.rule.name} (${sectionLabel(entry.rule.section)})`}
        callout={entry.explanation || entry.rule.text}
        testId={`feedback-similar-${entry.rule.id}${testSuffix}`}
      />
    </div>
  );
}

function SimilarRuleButton({ entry }) {
  const { navigate } = useNav();
  const { rule, score, method, explanation } = entry;
  const scorePct = Math.round(score * 100);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(VIEWS.rule(rule.id))}
      className="block w-full text-left rounded hover:bg-card/70 transition cursor-pointer"
      data-testid={`similar-rule-${rule.id}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <h4 className="font-medium truncate">{rule.name}</h4>
          <Badge variant="outline" className={sectionBadgeColor(rule.section)}>
            {sectionLabel(rule.section)}
          </Badge>
          <RuleCardsBadge ruleId={rule.id} count={rule.usage_count} />
          {rule.duplicate_count > 1 && (
            <Badge variant="outline" className="text-rose-300 border-rose-700/60 bg-rose-950/30">
              {rule.duplicate_count}× dup
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-xs font-mono ${
              scorePct >= 80 ? "text-rose-400" : scorePct >= 60 ? "text-amber-400" : "text-muted-foreground"
            }`}
          >
            {scorePct}% {method}
          </span>
        </div>
      </div>
      <p className="text-sm text-foreground/85 leading-snug mb-1">{rule.text}</p>
      {explanation && (
        <p className="text-xs text-muted-foreground italic">→ {explanation}</p>
      )}
    </div>
  );
}

function ConflictCard({ c, pdfTotalPages }) {
  const key = feedbackKey.conflict(c);
  const verdict = useVerdict(key);
  return (
    <div className={`transition-opacity ${isNegative(verdict) ? "opacity-55" : ""}`}>
      <ConflictCardBody c={c} pdfTotalPages={pdfTotalPages} />
      <div className="px-3 pb-1">
        <CalloutFeedback
          kind="conflict"
          targetKey={key}
          label={`${c.source === "steamroller" ? "Steamroller" : "Rulebook"}: ${c.section_title}`}
          callout={c.explanation}
          testId={`feedback-conflict-${c.source || "rulebook"}-${c.section_id}`}
        />
      </div>
    </div>
  );
}

function ConflictCardBody({ c, pdfTotalPages }) {
  const { navigate } = useNav();
  const sevColor = {
    high: "text-rose-400 border-rose-700/60",
    medium: "text-amber-400 border-amber-700/60",
    low: "text-cyan-400 border-cyan-700/60",
  }[c.severity] || "text-muted-foreground border-border";
  const isSr = c.source === "steamroller";
  const totalForSource = isSr
    ? (pdfTotalPages?.steamroller || null)
    : (pdfTotalPages?.rulebook || null);
  const SrcIcon = isSr ? Trophy : BookOpen;
  const srcLabel = isSr ? "Steamroller" : "Main rulebook";
  const srcColor = isSr ? "text-amber-400 border-amber-700/60" : "text-cyan-400 border-cyan-700/60";
  const goSection = (e) => {
    e?.stopPropagation();
    if (!c.section_id) return;
    if (isSr) navigate(VIEWS.steamrollerSection(c.section_id));
    else navigate(VIEWS.rulebookSection(c.section_id));
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={goSection}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goSection(e); } }}
      className="block w-full text-left rounded-md border p-3 border-border hover:border-primary/60 hover:bg-card/70 transition cursor-pointer"
      data-testid={`conflict-${c.source || "rulebook"}-${c.section_id}`}
      data-severity={c.severity}
      data-source={c.source || "rulebook"}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <h4 className="font-medium flex items-center gap-1.5">
          <SrcIcon className={`w-3.5 h-3.5 ${isSr ? "text-amber-400" : "text-cyan-400"}`} />
          {c.section_title}
          <span className="text-[10px] text-muted-foreground font-normal ml-1">
            → open section
          </span>
        </h4>
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className={`text-[10px] ${srcColor}`}>
            {srcLabel}
          </Badge>
          <Badge variant="outline" className={sevColor}>
            {c.severity}
          </Badge>
        </div>
      </div>
      <p className="text-sm leading-relaxed mb-2">
        <LinkifiedText
          text={c.explanation || ""}
          source={c.source === "steamroller" ? "steamroller" : "rulebook"}
          currentPages={[]}
          pdfTotalPages={totalForSource}
        />
      </p>
      {c.relevant_excerpt && (
        <blockquote className="text-xs text-muted-foreground border-l-2 border-border pl-3 italic">
          &ldquo;
          <LinkifiedText
            text={c.relevant_excerpt}
            source={c.source === "steamroller" ? "steamroller" : "rulebook"}
            currentPages={[]}
            pdfTotalPages={totalForSource}
          />
          &rdquo;
        </blockquote>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Conflicts panel
const SEVERITY_ORDER = { high: 0, medium: 1, low: 2 };
const SEVERITY_LABEL = { high: "High", medium: "Medium", low: "Low" };
const SEVERITY_COLOR = {
  high:   "text-rose-300 border-rose-700/60 bg-rose-950/30",
  medium: "text-amber-300 border-amber-700/60 bg-amber-950/30",
  low:    "text-cyan-300 border-cyan-700/60 bg-cyan-950/30",
};

export function ConflictsPanel({ conflicts }) {
  const [sevFilter, setSevFilter] = useState("all"); // all | high | medium-plus | low
  const [openRB, setOpenRB] = useState(true);
  const [openSR, setOpenSR] = useState(true);
  const [pdfTotalPages, setPdfTotalPages] = useState({ rulebook: 0, steamroller: 0 });

  useEffect(() => {
    let mounted = true;
    getPdfTotalPages().then((p) => { if (mounted) setPdfTotalPages(p); });
    return () => { mounted = false; };
  }, []);

  if (!conflicts.length) {
    return (
      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold mb-1 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-400" />
            Potential rule conflicts
            <Badge variant="outline" className="ml-1">0</Badge>
          </h3>
          <p className="text-sm text-muted-foreground italic mt-2">
            <Check className="w-4 h-4 text-emerald-500 inline mr-1" />
            No rulebook or Steamroller conflicts detected.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Counts
  const sevCounts = { high: 0, medium: 0, low: 0 };
  for (const c of conflicts) {
    const s = c.severity in sevCounts ? c.severity : "low";
    sevCounts[s] += 1;
  }
  const rbAll = conflicts.filter((c) => (c.source || "rulebook") === "rulebook");
  const srAll = conflicts.filter((c) => c.source === "steamroller");
  const distinctRbSections = new Set(rbAll.map((c) => c.section_id)).size;
  const distinctSrSections = new Set(srAll.map((c) => c.section_id)).size;

  const passes = (c) => {
    if (sevFilter === "all") return true;
    if (sevFilter === "high") return c.severity === "high";
    if (sevFilter === "medium-plus") return c.severity === "high" || c.severity === "medium";
    if (sevFilter === "low") return c.severity === "low";
    return true;
  };
  const sortByOrder = (a, b) => {
    const sa = SEVERITY_ORDER[a.severity] ?? 9;
    const sb = SEVERITY_ORDER[b.severity] ?? 9;
    if (sa !== sb) return sa - sb;
    return (a.section_title || "").localeCompare(b.section_title || "");
  };
  const rb = rbAll.filter(passes).sort(sortByOrder);
  const sr = srAll.filter(passes).sort(sortByOrder);

  return (
    <Card data-testid="conflicts-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-400" />
            Potential rule conflicts
            <Badge variant="outline" className="ml-1">{conflicts.length}</Badge>
          </h3>
          <div className="flex items-center gap-1.5" data-testid="severity-filter">
            {[
              { id: "all",         label: "All" },
              { id: "high",        label: "High" },
              { id: "medium-plus", label: "High + Medium" },
              { id: "low",         label: "Low" },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setSevFilter(f.id)}
                className={`text-xs px-2 py-0.5 rounded border transition ${
                  sevFilter === f.id
                    ? "border-primary/60 bg-primary/15 text-primary"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`severity-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary chip row */}
        <div className="flex items-center gap-2 flex-wrap mb-4 text-xs" data-testid="conflicts-summary">
          {["high", "medium", "low"].map((sev) => (
            <span
              key={sev}
              className={`px-2 py-0.5 rounded border ${SEVERITY_COLOR[sev]}`}
              data-testid={`severity-chip-${sev}`}
            >
              <strong className="tabular-nums">{sevCounts[sev]}</strong>
              <span className="opacity-80"> {SEVERITY_LABEL[sev]}</span>
            </span>
          ))}
          <span className="text-muted-foreground">
            across <strong className="text-foreground">{distinctRbSections + distinctSrSections}</strong> sections
            {" · "}
            <span className="text-cyan-300">{distinctRbSections} rulebook</span>
            {" / "}
            <span className="text-amber-300">{distinctSrSections} steamroller</span>
          </span>
        </div>

        <div className="space-y-3">
          {/* Main Rulebook group */}
          <Collapsible open={openRB} onOpenChange={setOpenRB} data-testid="conflicts-group-rulebook">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center justify-between gap-2 p-2 rounded border border-cyan-800/40 bg-cyan-950/20 hover:bg-cyan-900/30 transition"
                data-testid="conflicts-group-rulebook-toggle"
              >
                <span className="flex items-center gap-2 text-sm font-medium text-cyan-300">
                  <BookOpen className="w-4 h-4" /> Main Rulebook
                  <Badge variant="outline" className="text-[10px] border-cyan-700/60 text-cyan-400">
                    {rb.length}{sevFilter === "all" ? "" : ` / ${rbAll.length}`}
                  </Badge>
                </span>
                <ChevronDown className={`w-4 h-4 text-cyan-400 transition-transform ${openRB ? "rotate-180" : ""}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              {rb.length === 0 ? (
                <p className="text-xs italic text-muted-foreground pl-1">No rulebook conflicts match this filter.</p>
              ) : (
                <div className="space-y-3" data-testid="conflicts-group-rulebook-list">
                  {rb.map((c, i) => <ConflictCard key={`rb-${c.section_id}-${i}`} c={c} pdfTotalPages={pdfTotalPages} />)}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* Steamroller group */}
          <Collapsible open={openSR} onOpenChange={setOpenSR} data-testid="conflicts-group-steamroller">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center justify-between gap-2 p-2 rounded border border-amber-800/40 bg-amber-950/20 hover:bg-amber-900/30 transition"
                data-testid="conflicts-group-steamroller-toggle"
              >
                <span className="flex items-center gap-2 text-sm font-medium text-amber-300">
                  <Trophy className="w-4 h-4" /> Steamroller (Organized Play)
                  <Badge variant="outline" className="text-[10px] border-amber-700/60 text-amber-400">
                    {sr.length}{sevFilter === "all" ? "" : ` / ${srAll.length}`}
                  </Badge>
                </span>
                <ChevronDown className={`w-4 h-4 text-amber-400 transition-transform ${openSR ? "rotate-180" : ""}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              {sr.length === 0 ? (
                <p className="text-xs italic text-muted-foreground pl-1">No Steamroller conflicts match this filter.</p>
              ) : (
                <div className="space-y-3" data-testid="conflicts-group-steamroller-list">
                  {sr.map((c, i) => <ConflictCard key={`sr-${c.section_id}-${i}`} c={c} pdfTotalPages={pdfTotalPages} />)}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>
      </CardContent>
    </Card>
  );
}


// ---------------------------------------------------------------- Suggested revised wording
function ComparisonView({ textA, textB, resultA, resultB, comparison, savedId }) {
  return (
    <div className="space-y-4" data-testid="comparison-view">
      {savedId && (
        <p className="text-xs text-muted-foreground" data-testid="saved-indicator">
          <Check className="w-3 h-3 text-emerald-400 inline mr-1" />
          Saved to history (id <code>{savedId}</code>)
        </p>
      )}
      <UsageCost usage={comparison?.usage} label="Both wordings + comparison" testId="compare-usage-cost" />
      {comparison?.summary && (
        <Card className="border-primary/40" data-testid="comparison-summary">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <GitCompare className="w-4 h-4 text-primary" />
              <h3 className="font-semibold">Comparison</h3>
              {typeof comparison.overlap_ratio === "number" && (
                <Badge variant="outline" className="ml-1">
                  {Math.round(comparison.overlap_ratio * 100)}% text overlap
                </Badge>
              )}
            </div>
            <p className="text-sm leading-relaxed">{comparison.summary}</p>
            {(comparison.only_in_a?.length || comparison.only_in_b?.length) > 0 && (
              <div className="grid md:grid-cols-2 gap-3 mt-3 text-xs">
                <div>
                  <p className="text-muted-foreground mb-1">Words only in A</p>
                  <div className="flex flex-wrap gap-1">
                    {(comparison.only_in_a || []).map((w) => (
                      <code key={w} className="px-1.5 py-0.5 rounded bg-amber-950/30 text-amber-300 border border-amber-700/40">{w}</code>
                    ))}
                    {(comparison.only_in_a || []).length === 0 && <span className="italic text-muted-foreground">none</span>}
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Words only in B</p>
                  <div className="flex flex-wrap gap-1">
                    {(comparison.only_in_b || []).map((w) => (
                      <code key={w} className="px-1.5 py-0.5 rounded bg-cyan-950/30 text-cyan-300 border border-cyan-700/40">{w}</code>
                    ))}
                    {(comparison.only_in_b || []).length === 0 && <span className="italic text-muted-foreground">none</span>}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div data-testid="result-panel-a">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Wording A</p>
          <p className="text-sm mb-3 p-3 rounded bg-amber-950/15 border border-amber-700/30">{textA}</p>
          <AnalysisResult
            result={resultA}
            exportContext={{
              name: "Compare — Wording A",
              proposed_text: textA,
              alternative_text: textB,
              alternative_result: resultB,
              comparison,
            }}
          />
        </div>
        <div data-testid="result-panel-b">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Wording B</p>
          <p className="text-sm mb-3 p-3 rounded bg-cyan-950/15 border border-cyan-700/30">{textB}</p>
          <AnalysisResult
            result={resultB}
            exportContext={{ name: "Compare — Wording B", proposed_text: textB }}
          />
        </div>
      </div>
    </div>
  );
}



// ============================================================ Pack Composer
// Multiple rules that all sit on the SAME model. The LLM analyzes each rule
// independently AND runs an extra "pack interactions" pass that presumes
// every rule applies to the same model so it can flag cross-rule
// contradictions, redundancies, unintended stacking and timing overlaps.

function emptyPackRule() {
  return { uid: crypto.randomUUID(), name: "", rule_type: "", proposed_text: "", intent: "", stats: {} };
}

function PackComposer({ seed }) {
  const { navigate } = useNav();
  const [packName, setPackName] = useShared("model_name", "");
  const [tags, setTags] = useShared("model_tags", []);
  const [ruleType, setRuleType] = useShared("model_rule_type", "");
  const [modelTypes, setModelTypes] = useShared("model_model_types", []);
  const [packIntent, setPackIntent] = useShared("model_intent", "");
  const [card, setCard] = useShared("model_card", null);
  const [rules, setRules] = useShared("model_rules", [emptyPackRule()]);
  useEffect(() => {
    if (!seed) return;
    setRuleType(seed.ruleType || "");
    setModelTypes(seed.modelTypes || []);
    setCard(seed.card || null);
    setRules(seed.rules?.length ? seed.rules.map((r) => ({ ...emptyPackRule(), ...r })) : [emptyPackRule()]);
  }, [seed?.key]);  // eslint-disable-line react-hooks/exhaustive-deps
  const [loading, setLoading] = useShared("model_loading", false);
  const [error, setError] = useShared("model_error", null);
  const [progress, setProgress] = useShared("model_progress", null);  // job state during analyze
  const [packResult, setPackResult] = useShared("model_pack_result", null);  // {rules:[...], pack_interactions:[...]}
  const [savedPackId, setSavedPackId] = useShared("model_saved_id", null);
  const [elapsed, setElapsed] = useState(0);
  const [cancelSignal, setCancelSignal] = useState(null);
  const [activeJobId, setActiveJobId] = useShared("model_job_id", null);

  useEffect(() => {
    if (!loading) return;
    setElapsed(0);
    const start = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
    return () => clearInterval(t);
  }, [loading]);

  const addRule = () => setRules((rs) => [...rs, emptyPackRule()]);
  const removeRule = (i) => setRules((rs) => rs.filter((_, idx) => idx !== i));
  const me = useSharedDraft()?.me;
  const patchRule = (i, patch) => setRules((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch, edited_by: me?.name, edited_by_id: me?.id, edited_at: new Date().toISOString() } : r));

  const cardRules = (card?.rules || [])
    .filter((r) => r.is_new && (r.text || "").trim())
    .map((r) => ({ ...emptyPackRule(), name: r.name, rule_type: r.rule_type || "", proposed_text: r.text.trim(), fromCard: true }));
  const filled = rules.filter((r) => r.proposed_text.trim()).length + cardRules.length;
  const canSubmit = filled >= (card ? 1 : 2);

  const handleAnalyze = async () => {
    const cleaned = [...cardRules, ...rules]
      .filter((r) => r.proposed_text.trim())
      .map((r) => ({
        name: r.name.trim(),
        rule_type: r.rule_type || ruleType || null,
        proposed_text: r.proposed_text.trim(),
        intent: (r.intent || "").trim() || null,
        rule_stats: cleanStats(r.rule_type || ruleType, r.stats),
        alternative_text: (r.alternative_text || "").trim() || null,
      }));
    if (!canSubmit) return;
    const cardPayload = card && {
      name: card.name, text: card.text, card_id: card.card_id, image_path: card.image_path, image_paths: card.image_paths,
      placement: card.placement, source: card.source, rules: card.rules, icons: card.icons,
    };
    setLoading(true);
    setError(null);
    setPackResult(null);
    setSavedPackId(null);
    setProgress(null);
    const signal = { cancelled: false };
    setCancelSignal(signal);
    setActiveJobId(null);
    try {
      const final = await analyzePackStream(
        cleaned,
        ruleType || null,
        modelTypes,
        (job, jobId) => {
          if (jobId && !activeJobId) setActiveJobId(jobId);
          setProgress(job);
        },
        packIntent || null,
        signal,
        cardPayload,
      );
      setPackResult(final);
      // Auto-save the pack to history.
      try {
        const saved = await api.savePack({
          name: packName.trim() || card?.name || null,
          card: cardPayload,
          tags,
          rule_type: ruleType || null,
          model_types: modelTypes,
          intent: packIntent || null,
          rules: final.rules,
          pack_interactions: final.pack_interactions,
        });
        setSavedPackId(saved.id);
      } catch (e) {
        console.warn("save pack failed", e);
        setError("The model check finished but could not be saved to History — try running it again.");
      }
    } catch (e) {
      if (e?.cancelled) {
        setError("Pack analysis cancelled.");
      } else {
        setError(e?.message || "Pack analysis failed");
      }
    } finally {
      setLoading(false);
      setCancelSignal(null);
      setActiveJobId(null);
    }
  };

  const handleCancel = async () => {
    if (cancelSignal) cancelSignal.cancelled = true;
    if (activeJobId) {
      try { await api.cancelPack(activeJobId); } catch (e) { console.warn("Cancel pack failed (best-effort)", e); }
    }
    setError("Pack analysis cancelled.");
    setLoading(false);
  };

  useRunWatcher({
    loading, localRunning: Boolean(cancelSignal), kind: "pack", jobId: activeJobId,
    onComplete: (job) => {
      setProgress(job);
      setPackResult({ rules: job.rules, pack_interactions: job.pack_interactions, usage: job.usage, job_id: job.id });
      setLoading(false); setActiveJobId(null);
    },
    onStop: (msg) => { setError(msg); setLoading(false); setActiveJobId(null); },
  });

  return (
    <div className="grid gap-6" data-testid="pack-composer">
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">Model check</h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(VIEWS.history())}
              className="gap-1.5"
              data-testid="open-history-btn-pack"
            >
              <History className="w-4 h-4" /> History
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Give the LLM the whole model card and one or more new rules for it. Each rule is QA&rsquo;d against the
            rulebook and Steamroller, then a cross-check looks at how the new rules interact with each other and with the
            card&rsquo;s existing abilities, weapons, spells and feat. Saved to History under the model&rsquo;s name.
          </p>

          <CardSourcePicker card={card} onChange={setCard} disabled={loading} optional />
          <EditedBy field="model_card" />
          {!card && <p className="text-[11px] text-muted-foreground">Without a card this works like the old rule pack: add 2+ rules that sit on the same model.</p>}

          <div className="grid md:grid-cols-3 gap-2">
            <Select value={ruleType} onValueChange={setRuleType} disabled={loading}>
              <SelectTrigger className="w-full" data-testid="pack-rule-type-select">
                <SelectValue placeholder="Default rule type — overridden per-rule…" />
              </SelectTrigger>
              <SelectContent>
                {RULE_TYPES.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`pack-rule-type-option-${t.id}`}>
                    <span className="font-medium">{t.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={packName}
              onChange={(e) => setPackName(e.target.value)}
              placeholder={card?.name ? `Saved as “${card.name}” — or name it…` : "Name for this check…"}
              disabled={loading}
              data-testid="pack-name-input"
            />
            <TagsInput
              value={tags}
              onChange={setTags}
              testId="pack-tags"
              placeholder="Tags…"
              disabled={loading}
            />
          </div>

          <ModelTypeSelect
            value={modelTypes}
            onChange={setModelTypes}
            disabled={loading}
            testId="pack-model-types"
          />

          {/* Pack-level intent — applies to all rules in the pack */}
          <details className="rounded border border-border bg-card/30">
            <summary className="cursor-pointer px-3 py-2 text-xs text-muted-foreground hover:text-foreground select-none" data-testid="pack-intent-toggle">
              <MessageSquare className="w-3 h-3 inline mr-1" />
              Overall intent <EditedBy field="model_intent" /> <span className="opacity-70">(optional — what the new rules are meant to achieve on this model and how they should/shouldn’t combine)</span>
            </summary>
            <div className="p-3 pt-1">
              <Textarea
                value={packIntent}
                onChange={(e) => setPackIntent(e.target.value)}
                placeholder={`What this pack is meant to do on the model: …\nWhat the rules should NOT do together: …\nSuspected interaction concerns: …`}
                className="min-h-[90px] font-sans text-sm"
                disabled={loading}
                data-testid="pack-intent-textarea"
              />
            </div>
          </details>
        </CardContent>
      </Card>

      {/* Per-rule editors */}
      <div className="space-y-3" data-testid="pack-rule-list">
        {rules.map((r, i) => (
          <PackRuleEditor
            key={r.uid}
            index={i}
            rule={r}
            packRuleType={ruleType}
            disabled={loading}
            onChange={(patch) => patchRule(i, patch)}
            onRemove={rules.length > 1 ? () => removeRule(i) : null}
            progress={progress?.rules?.[i]}
          />
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={addRule}
          disabled={loading || rules.length >= 10}
          className="gap-1.5"
          data-testid="pack-add-rule-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add another rule
        </Button>
        <RunningBy field="model_loading" />
        {loading && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            className="gap-1.5 text-rose-300 border-rose-700/60 hover:bg-rose-950/30"
            data-testid="pack-cancel-btn"
          >
            <X className="w-3.5 h-3.5" /> Cancel pack analysis
          </Button>
        )}
        <Button
          onClick={handleAnalyze}
          disabled={loading || !canSubmit}
          className="gap-2 ml-auto"
          data-testid="pack-analyze-btn"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing pack… {elapsed}s</>
          ) : (
            <><Sparkles className="w-4 h-4" /> Run model check ({filled} rule{filled === 1 ? "" : "s"})</>
          )}
        </Button>
      </div>

      {progress && progress.interactions_status && (
        <div className="text-xs text-muted-foreground" data-testid="pack-progress-message">
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin text-primary" />
            {progress.message || "Working…"}
          </span>
          {" "}— rules done: {progress.rules_done || 0}/{progress.rules_total || 0}
          {progress.interactions_status === "running" && " · running pack interactions…"}
        </div>
      )}

      {error && (
        <Alert variant="destructive" data-testid="pack-error">
          <AlertTriangle className="w-4 h-4" />
          <AlertTitle>Pack analysis failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {packResult && (
        <PackResultView
          packResult={packResult}
          savedPackId={savedPackId}
          ruleType={ruleType}
          modelTypes={modelTypes}
        />
      )}
    </div>
  );
}

function PackRuleEditor({ index, rule, packRuleType, disabled, onChange, onRemove, progress }) {
  const stages = progress?.stages || {};
  const ruleResult = progress?.result || null;
  const conflictCount = (ruleResult?.conflicts || progress?.partial?.conflicts || []).length;
  const semCount = (ruleResult?.semantic_similar || progress?.partial?.semantic_similar || []).length;
  return (
    <Card data-testid={`pack-rule-${index}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Badge variant="outline" className="text-primary border-primary/40 bg-primary/10">Rule {index + 1}</Badge>
          {rule.edited_by && <TypingMark name={rule.edited_by} byId={rule.edited_by_id} at={rule.edited_at} testId={`pack-rule-${index}-edited-by`} />}
          <Input
            value={rule.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="Rule name (optional)…"
            className="max-w-[260px] h-7 text-sm"
            disabled={disabled}
            data-testid={`pack-rule-${index}-name`}
          />
          <Select value={rule.rule_type} onValueChange={(v) => onChange({ rule_type: v })} disabled={disabled}>
            <SelectTrigger className="max-w-[180px] h-7 text-sm" data-testid={`pack-rule-${index}-type`}>
              <SelectValue placeholder="Rule type…" />
            </SelectTrigger>
            <SelectContent>
              {RULE_TYPES.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {progress && (
            <div className="flex items-center gap-1.5 text-[10px]">
              {Object.entries(stages).map(([k, s]) => (
                <span
                  key={k}
                  className={`px-1.5 py-0.5 rounded border ${
                    s === "done" ? "border-emerald-700/60 bg-emerald-950/30 text-emerald-300"
                      : s === "running" ? "border-primary/60 bg-primary/15 text-primary"
                      : "border-border text-muted-foreground"
                  }`}
                  data-testid={`pack-rule-${index}-stage-${k}`}
                >
                  {k}
                </span>
              ))}
              {ruleResult && (
                <span className="text-rose-300 ml-1" data-testid={`pack-rule-${index}-conflict-count`}>
                  {conflictCount} conflicts · {semCount} similar
                </span>
              )}
            </div>
          )}
          {onRemove && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-7 px-2 text-muted-foreground hover:text-rose-300"
              onClick={onRemove}
              disabled={disabled}
              data-testid={`pack-rule-${index}-remove`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
        <div className="mb-2 empty:hidden">
          <StatBlockEditor
            ruleType={rule.rule_type || packRuleType}
            value={rule.stats}
            onChange={(stats) => onChange({ stats })}
            disabled={disabled}
            testId={`pack-rule-${index}-stat-block`}
          />
        </div>
        <Textarea
          value={rule.proposed_text}
          onChange={(e) => onChange({ proposed_text: e.target.value })}
          placeholder="Proposed rule wording…"
          className="min-h-[90px] font-sans"
          disabled={disabled}
          data-testid={`pack-rule-${index}-text`}
        />
        <details className="mt-2 rounded border border-border bg-card/30">
          <summary className="cursor-pointer px-3 py-1.5 text-[11px] text-muted-foreground hover:text-foreground select-none" data-testid={`pack-rule-${index}-intent-toggle`}>
            <MessageSquare className="w-3 h-3 inline mr-1" />
            Intent for this rule <span className="opacity-70">(optional — overrides the overall intent)</span>
          </summary>
          <div className="p-2 pt-1">
            <Textarea
              value={rule.intent || ""}
              onChange={(e) => onChange({ intent: e.target.value })}
              placeholder="What this rule is meant to do / not do, and which rules you think it might conflict with…"
              className="min-h-[70px] font-sans text-sm"
              disabled={disabled}
              data-testid={`pack-rule-${index}-intent`}
            />
          </div>
        </details>
        <details className="rounded border border-border bg-card/30 mt-2" open={Boolean(rule.alternative_text)}>
          <summary className="cursor-pointer px-3 py-2 text-xs text-muted-foreground hover:text-foreground select-none" data-testid={`pack-rule-${index}-alt-toggle`}>
            <GitCompare className="w-3 h-3 inline mr-1" />
            Alternative wording <span className="opacity-70">(optional — the LLM says if it avoids an issue)</span>
          </summary>
          <div className="p-2 pt-1">
            <Textarea
              value={rule.alternative_text || ""}
              onChange={(e) => onChange({ alternative_text: e.target.value })}
              placeholder="Another way you're considering wording this rule…"
              className="min-h-[60px] font-sans text-sm"
              disabled={disabled}
              data-testid={`pack-rule-${index}-alt`}
            />
          </div>
        </details>
      </CardContent>
    </Card>
  );
}

const INTERACTION_KIND_STYLES = {
  contradiction:           { label: "Contradiction",         klass: "text-rose-300 border-rose-700/60 bg-rose-950/30" },
  redundancy:              { label: "Redundancy",            klass: "text-amber-300 border-amber-700/60 bg-amber-950/30" },
  stacks_unintentionally:  { label: "Stacks badly",          klass: "text-orange-300 border-orange-700/60 bg-orange-950/30" },
  timing_overlap:          { label: "Timing overlap",        klass: "text-cyan-300 border-cyan-700/60 bg-cyan-950/30" },
  note:                    { label: "Note",                  klass: "text-muted-foreground border-border bg-muted/10" },
};

function InteractionItem({ it, index, children }) {
  const key = feedbackKey.interaction(it);
  const verdict = useVerdict(key);
  return (
    <div className={`p-3 rounded border border-border bg-card/40 transition-opacity ${isNegative(verdict) ? "opacity-55" : ""}`} data-testid={`pack-interaction-${index}`}>
      {children}
      <CalloutFeedback
        kind="interaction"
        targetKey={key}
        label={`${(it.rule_names || []).join(" + ")} (${it.kind || "note"})`}
        callout={it.explanation}
        testId={`feedback-interaction-${index}`}
      />
    </div>
  );
}

export function PackResultView(props) {
  const { packResult } = props;
  const scopeText = (packResult.rules || []).map((r) => r.proposed_text).join("\n---\n");
  return (
    <CalloutFeedbackProvider scopeText={scopeText} ruleType={props.ruleType} keys={(packResult.pack_interactions || []).map(feedbackKey.interaction)}>
      <PackResultViewBody {...props} />
    </CalloutFeedbackProvider>
  );
}

function PackResultViewBody({ packResult, savedPackId, ruleType, modelTypes }) {
  const { rules = [], pack_interactions = [] } = packResult;
  const sevCounts = { high: 0, medium: 0, low: 0 };
  for (const it of pack_interactions) {
    if (it.severity in sevCounts) sevCounts[it.severity]++;
  }
  return (
    <div className="space-y-5" data-testid="pack-result">
      {savedPackId && (
        <p className="text-xs text-muted-foreground" data-testid="pack-saved-indicator">
          <Check className="w-3 h-3 text-emerald-400 inline mr-1" />
          Pack saved to history (id <code>{savedPackId}</code>)
        </p>
      )}

      <UsageCost usage={packResult.usage} label="Whole pack" testId="pack-usage-cost" />

      {/* Pack interactions — the cross-rule LLM pass */}
      <Card className={pack_interactions.length ? "border-violet-700/40" : ""} data-testid="pack-interactions-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Brain className="w-4 h-4 text-violet-400" />
            <h3 className="font-semibold">Cross-rule interactions on this model</h3>
            <Badge variant="outline" className="ml-1">{pack_interactions.length}</Badge>
            <div className="ml-auto flex gap-1.5 text-[10px]">
              {sevCounts.high > 0 && <span className="px-1.5 py-0.5 rounded border border-rose-700/60 bg-rose-950/30 text-rose-300">{sevCounts.high} high</span>}
              {sevCounts.medium > 0 && <span className="px-1.5 py-0.5 rounded border border-amber-700/60 bg-amber-950/30 text-amber-300">{sevCounts.medium} medium</span>}
              {sevCounts.low > 0 && <span className="px-1.5 py-0.5 rounded border border-cyan-700/60 bg-cyan-950/30 text-cyan-300">{sevCounts.low} low</span>}
            </div>
          </div>
          {pack_interactions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              <Check className="w-4 h-4 text-emerald-500 inline mr-1" />
              No cross-rule interactions flagged — the rules look like they coexist cleanly on the same model.
            </p>
          ) : (
            <div className="space-y-2" data-testid="pack-interactions-list">
              {pack_interactions.map((it, i) => {
                const ks = INTERACTION_KIND_STYLES[it.kind] || INTERACTION_KIND_STYLES.note;
                const sevColor = {
                  high: "text-rose-300 border-rose-700/60 bg-rose-950/30",
                  medium: "text-amber-300 border-amber-700/60 bg-amber-950/30",
                  low: "text-cyan-300 border-cyan-700/60 bg-cyan-950/30",
                }[it.severity] || "text-muted-foreground border-border";
                return (
                  <InteractionItem key={i} it={it} index={i}>
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <Badge variant="outline" className={`text-[10px] ${ks.klass}`}>{ks.label}</Badge>
                      <Badge variant="outline" className={`text-[10px] ${sevColor}`}>{it.severity}</Badge>
                      {(it.rule_names || []).map((n, j) => (
                        <Badge key={j} variant="outline" className="text-[10px] text-primary border-primary/40 bg-primary/10">
                          {n}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-sm leading-relaxed">{it.explanation}</p>
                  </InteractionItem>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-rule analyses */}
      <div className="space-y-6" data-testid="pack-per-rule-results">
        {rules.map((r, i) => (
          <Card key={r.id || i} className="border-primary/30" data-testid={`pack-rule-result-${i}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Badge variant="outline" className="text-primary border-primary/40 bg-primary/10">Rule {i + 1}</Badge>
                <strong>{r.name || "(unnamed)"}</strong>
                {r.rule_type && (
                  <Badge variant="outline" className="text-violet-300 border-violet-700/60 bg-violet-950/30 text-[10px]">
                    {ruleTypeLabel(r.rule_type)}
                  </Badge>
                )}
              </div>
              <p className="text-sm leading-relaxed bg-background/40 rounded p-3 border border-border mb-3">
                {r.proposed_text}
              </p>
              {r.result ? (
                <AnalysisResult
                  result={r.result}
                  persistRevision={savedPackId && r.id ? (history) => api.updatePack(savedPackId, { rule_revision: { rule_id: r.id, history } }) : null}
                  exportContext={{
                    name: r.name || `Rule ${i + 1}`,
                    rule_type: r.rule_type || ruleType || null,
                    model_types: modelTypes,
                    intent: r.intent || null,
                    proposed_text: r.proposed_text,
                  }}
                />
              ) : (
                <p className="text-sm text-muted-foreground italic">No result for this rule.</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
